import React from "react";

import { isMobile } from "react-device-detect";

import Breadcrumb from "@/components/Breadcrumb";
import Button from "@/components/Button";
import Inner from "@/components/Inner";
import LocaleText from "@/components/Intl/LocaleText";
import RecommendationCard from "@/components/RecommandationCard";
import Title from "@/components/Title";
import Spinner from "@/components/Spinner";
import { ArrowLeft } from "lucide-react";

import { ProcessContext } from "..";
import { TCustomerAnswerResponse } from "@/helpers/API/requests/interfaces/customer";

import { TCartStep } from "./interfaces";
import Styles from "./styles.module.scss";

const CartStep: React.FC<TCartStep> = ({
  prev,
  products,
  onSubmit,
  goToPreviousStep,
}) => {
  const { recommendations, customProducts } = React.useContext(ProcessContext);
  const { routines } = recommendations as TCustomerAnswerResponse;
  const hasAutoSubmitted = React.useRef<boolean>(false);

  const routinesFiltered = React.useMemo(
    () =>
      products
        ? routines.filter((routine, parentIndex) => {
            const productIds = products.map((product) => product.id);

            const hasSelectedProduct =
              routine.proposed_products.some(({ id }) =>
                productIds.includes(id),
              ) ||
              routine.sub_routines.some((sub) =>
                sub.proposed_products.some(({ id }) =>
                  productIds.includes(id),
                ),
              );

            const hasCustomProduct = customProducts.some(
              (p) =>
                (p.routinePosition === routine.position &&
                  p.parentRoutinePosition === undefined) ||
                p.parentRoutinePosition === parentIndex,
            );

            return hasSelectedProduct || hasCustomProduct;
          })
        : [],
    [products, routines, customProducts],
  );

  const isCartEmpty = Boolean(products) && routinesFiltered.length === 0;

  // `onSubmit` est recréé à chaque render du parent : le ref garantit un seul
  // envoi, l'effet ne pouvant pas s'appuyer sur son identité.
  React.useEffect(
    function autoSubmitEmptyCart() {
      if (isCartEmpty && !hasAutoSubmitted.current) {
        hasAutoSubmitted.current = true;
        onSubmit();
      }
    },
    [isCartEmpty, onSubmit],
  );

  if (products) {
    return (
      <div className={Styles["cartStep"]}>
        <Breadcrumb
          step={isMobile ? 3 : 4}
          displayBackButton={true}
          onBackClick={goToPreviousStep}
        />
        <Inner type="md" className={Styles["cartStep__heading"]}>
          <Title className={Styles["cartStep__title"]}>
            <LocaleText>page.cartStep.title</LocaleText>
          </Title>
          <Title className={Styles["cartStep__subtitle"]}>
            <LocaleText>page.cartStep.subtitle</LocaleText>
          </Title>
        </Inner>

        <div className={Styles["cartStep__body"]}>
          {routinesFiltered.map((routine, rIndex) => {
            const parentIndex = routines.indexOf(routine);
            const matchingProducts = [];

            const isProductSelected = (productId: number) =>
              products.some(
                (selectedProduct) => selectedProduct.id === productId,
              );

            for (const product of routine.proposed_products) {
              if (isProductSelected(product.id)) {
                matchingProducts.push(product);
              }
            }

            for (const subRoutine of routine.sub_routines) {
              for (const product of subRoutine.proposed_products) {
                if (isProductSelected(product.id)) {
                  matchingProducts.push(product);
                }
              }
            }

            const routineCustomProducts = customProducts.filter(
              (p) => p.routinePosition === routine.position && p.parentRoutinePosition === undefined,
            );
            const subRoutineCustomProducts = routine.sub_routines.flatMap((_, subRIndex) =>
              customProducts.filter(
                (p) => p.routinePosition === subRIndex && p.parentRoutinePosition === parentIndex,
              ),
            );
            matchingProducts.push(...routineCustomProducts, ...subRoutineCustomProducts);

            if (matchingProducts.length > 0) {
              return (
                <div
                  key={routine.position}
                  className={Styles.cartStep__routine__container}
                >
                  <div className={Styles.cartStep__routine__title}>
                    <span>{rIndex + 1}.</span>
                    {routine.title}
                  </div>
                  {matchingProducts.map((product) => (
                    <RecommendationCard
                      key={`recommendationCard-${product.id}`}
                      className={Styles.cartStep__routine__card}
                      {...product}
                      isInCart
                    />
                  ))}
                </div>
              );
            }

            return null;
          })}
        </div>

        <div className={Styles["cartStep__footer"]}>
          <Inner type="md" className={Styles["cartStep__footer-inner"]}>
            <p className={Styles["cartStep__footer-selected"]}>
              {products.length}{" "}
              <LocaleText plural={products.length !== 1}>
                page.cartStep.selectedProduct
              </LocaleText>
            </p>
            <Button onClick={onSubmit} className={Styles["cartStep__button"]}>
              <LocaleText>page.cartStep.submit</LocaleText>
            </Button>
          </Inner>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        className={Styles["cartStep__backIcon"]}
        onClick={goToPreviousStep}
      >
        <ArrowLeft />
      </button>
      <div className={Styles["cartStep"]}>
        <Breadcrumb step={isMobile ? 3 : 4} />
        <Spinner className={Styles["cartStep__loading"]} />
      </div>
    </>
  );
};

export default CartStep;
